import {
  Goal,
  goalsArchivedSectionID,
  goalsCompletedSectionID,
  ID,
  LifeArea,
  lifeAreaOtherID,
  rootId,
  TaskListItemTypes,
  TaskListTreeItem,
} from '@griply/types';

import { getArchivedGoalIds } from '../../derived-goal-state';
import {
  buildGoalsById,
  getGoalTopLevelLifeAreas,
} from '../../get-goal-life-area';
import { getPresentParentIds } from '../get-present-parent-ids';
import { orderGoalTreeChildren } from './order-goal-tree-children';
import { BuildGoalListTreeOptions } from './types';

export type BuildLifeAreaGoalListTreeOptions = BuildGoalListTreeOptions & {
  otherLifeAreaName: string;
};

export const buildLifeAreaGoalListTree = (
  goals: Goal[],
  lifeAreas: LifeArea[],
  {
    hideArchived,
    hideCompleted,
    otherLifeAreaName,
    allGoals,
    links,
    goalChildrenByParentId,
  }: BuildLifeAreaGoalListTreeOptions,
): Record<ID, TaskListTreeItem> => {
  const archivedIds = getArchivedGoalIds(allGoals, { links });
  const activeGoals: Goal[] = [];
  const archivedGoals: ID[] = [];
  const completedGoals: ID[] = [];

  goals.forEach((goal) => {
    if (hideArchived && archivedIds.has(goal.id)) {
      return;
    }
    if (goal.archivedAt) {
      archivedGoals.push(goal.id);
      return;
    }
    // A completed goal never sits inline among its live siblings; it moves to its own section.
    if (goal.completedAt) {
      if (!hideCompleted) {
        completedGoals.push(goal.id);
      }
      return;
    }
    activeGoals.push(goal);
  });

  const treeItems: Record<ID, TaskListTreeItem> = {};

  lifeAreas.forEach((lifeArea) => {
    treeItems[lifeArea.id] = {
      id: lifeArea.id,
      type: TaskListItemTypes.LifeArea,
      name: lifeArea.name,
      children: [],
      isFolder: true,
    };
  });

  treeItems[lifeAreaOtherID] = {
    id: lifeAreaOtherID,
    type: TaskListItemTypes.LifeArea,
    name: otherLifeAreaName,
    children: [],
    isFolder: true,
  };

  goals.forEach((goal) => {
    treeItems[goal.id] = {
      id: goal.id,
      type: TaskListItemTypes.Goal,
      name: goal.name,
      children: [],
      isFolder: true,
    };
  });

  const goalsById = buildGoalsById(allGoals);
  const lifeAreaCache = new Map<ID, ID[]>();

  activeGoals.forEach((goal) => {
    const parentIds = getPresentParentIds(goal, (id) => !!treeItems[id], {
      links,
      goalsById,
    });
    const isNested = !!parentIds.length;

    // A goal nests under EVERY parent present in the set; the tree list keys nodes by path.
    parentIds.forEach((parentId) => {
      treeItems[parentId].children?.push(goal.id);
    });

    // Archived through an ancestor the filter dropped — no parent left to nest under.
    if (!isNested && archivedIds.has(goal.id)) {
      archivedGoals.push(goal.id);
      return;
    }

    // The renderer keys nodes by path, so pushing one goal id into several life areas renders it under each.
    const targetIds = new Set(
      getGoalTopLevelLifeAreas(goal, {
        isNested,
        goalsById,
        links,
        cache: lifeAreaCache,
      }).filter((lifeAreaId) => treeItems[lifeAreaId]),
    );

    if (targetIds.size) {
      targetIds.forEach((targetId) => {
        treeItems[targetId].children?.push(goal.id);
      });
      return;
    }

    if (!isNested) {
      treeItems[lifeAreaOtherID].children?.push(goal.id);
    }
  });

  if (completedGoals.length) {
    treeItems[goalsCompletedSectionID] = {
      id: goalsCompletedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: '',
      children: completedGoals,
      isFolder: true,
    };
  }

  if (archivedGoals.length) {
    treeItems[goalsArchivedSectionID] = {
      id: goalsArchivedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: '',
      children: archivedGoals,
      isFolder: true,
    };
  }

  const rootChildren = lifeAreas.reduce<ID[]>((acc, lifeArea) => {
    if (treeItems[lifeArea.id].children?.length) {
      acc.push(lifeArea.id);
    }
    return acc;
  }, []);

  if (treeItems[lifeAreaOtherID]?.children?.length) {
    rootChildren.push(lifeAreaOtherID);
  }

  if (completedGoals.length) {
    rootChildren.push(goalsCompletedSectionID);
  }

  if (archivedGoals.length) {
    rootChildren.push(goalsArchivedSectionID);
  }

  orderGoalTreeChildren(treeItems, goalChildrenByParentId);

  return {
    [rootId]: {
      id: rootId,
      name: 'Root',
      type: TaskListItemTypes.Root,
      children: rootChildren,
    },
    ...treeItems,
  };
};
